import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import {
  Users, Plus, Edit3, Trash2, X, AlertTriangle, CheckCircle, Loader2,
  Gavel, FileText, ShieldAlert, Info, Clock, User, Briefcase, Landmark,
} from 'lucide-react';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC','PR','VI','GU','AS','MP','AA','AE','AP'];

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch { return iso; }
};

const TYPE_META = {
  individual: { label: 'Individual', icon: User },
  trust:      { label: 'Trust',      icon: Briefcase },
  estate:     { label: 'Estate',     icon: Landmark },
};


// ────────────────────────────────────────────────────────────────
// Add / Edit modal
// ────────────────────────────────────────────────────────────────
function BeneficiaryFormModal({ open, initial, defaultTier, remainingPct, onClose, onSaved }) {
  const [form, setForm] = useState({
    tier: defaultTier || 'primary',
    name: '', relationship: '',
    ssn: '', dateOfBirth: '',
    street: '', city: '', state: '', zip: '',
    allocationPct: '', beneficiaryType: 'individual',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!initial) {
      setForm({
        tier: defaultTier || 'primary',
        name: '', relationship: '',
        ssn: '', dateOfBirth: '',
        street: '', city: '', state: '', zip: '',
        allocationPct: remainingPct > 0 ? String(remainingPct) : '',
        beneficiaryType: 'individual',
      });
    } else {
      setForm({
        tier: initial.tier,
        name: initial.name || '',
        relationship: initial.relationship || '',
        ssn: '', // never pre-populate SSN — re-enter on edit for safety
        dateOfBirth: initial.dateOfBirth || '',
        street: initial.street || '', city: initial.city || '',
        state: initial.state || '', zip: initial.zip || '',
        allocationPct: String(initial.allocationPct || ''),
        beneficiaryType: initial.beneficiaryType || 'individual',
      });
    }
    setErr('');
  }, [initial, defaultTier, remainingPct, open]);

  if (!open) return null;

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSSNChange = (raw) => {
    const digits = raw.replace(/\D/g, '').slice(0, 9);
    let formatted = digits;
    if (digits.length > 5) formatted = `${digits.slice(0,3)}-${digits.slice(3,5)}-${digits.slice(5)}`;
    else if (digits.length > 3) formatted = `${digits.slice(0,3)}-${digits.slice(3)}`;
    set('ssn', formatted);
  };

  const handleSave = async () => {
    setErr('');
    const pct = parseFloat(form.allocationPct);
    if (!form.name || !form.relationship || !form.dateOfBirth ||
        !form.street || !form.city || !form.state || !form.zip) {
      setErr('All fields are required.'); return;
    }
    if (isNaN(pct) || pct <= 0 || pct > 100) {
      setErr('Allocation must be between 0 and 100%.'); return;
    }
    if (!initial && !/^\d{3}-\d{2}-\d{4}$/.test(form.ssn)) {
      setErr('SSN must be 9 digits (e.g. 123-45-6789).'); return;
    }
    if (initial && form.ssn && !/^\d{3}-\d{2}-\d{4}$/.test(form.ssn)) {
      setErr('If changing SSN, enter full 9 digits (e.g. 123-45-6789).'); return;
    }

    const body = {
      tier: form.tier,
      name: form.name.trim(),
      relationship: form.relationship.trim(),
      ssn: form.ssn || '000000000', // placeholder (PUT re-encrypts; if unchanged, re-use last4 server-side)
      dateOfBirth: form.dateOfBirth,
      street: form.street.trim(),
      city: form.city.trim(),
      state: form.state,
      zip: form.zip.trim(),
      allocationPct: pct,
      beneficiaryType: form.beneficiaryType,
    };
    if (initial && !form.ssn) {
      setErr('Re-enter SSN to confirm the edit.'); return;
    }

    setSaving(true);
    try {
      if (initial?.id) {
        await api.put(`/participant/beneficiaries/${initial.id}`, body);
      } else {
        await api.post('/participant/beneficiaries', body);
      }
      onSaved?.();
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(Array.isArray(detail) ? detail[0]?.msg || 'Save failed.' : detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const input = 'w-full h-10 px-3 bg-white border border-[#D1D5DB] rounded text-sm text-[#1B2A4A] focus:outline-none focus:border-[#1B2A4A] focus:ring-1 focus:ring-[#1B2A4A]/20';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="beneficiary-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E2E5E9]">
          <h3 className="text-sm font-semibold text-[#1B2A4A]">
            {initial ? 'Edit Beneficiary' : 'Add Beneficiary'}
          </h3>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#374151]" data-testid="close-beneficiary-modal">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-3">
          {err && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="beneficiary-form-error">
              <AlertTriangle size={13} className="text-red-600" />
              <span className="text-xs text-red-700">{err}</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Tier</label>
              <div className="grid grid-cols-2 gap-2">
                {['primary','contingent'].map(t => (
                  <button key={t} type="button" onClick={() => set('tier', t)}
                          data-testid={`tier-${t}`}
                          className={`h-10 rounded border text-xs font-medium capitalize transition-colors ${form.tier === t ? 'bg-[#1B2A4A] text-white border-[#1B2A4A]' : 'bg-white text-[#6B7280] border-[#D1D5DB] hover:border-[#1B2A4A]'}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Type</label>
              <select value={form.beneficiaryType} onChange={e => set('beneficiaryType', e.target.value)}
                      data-testid="input-beneficiary-type"
                      className={input}>
                <option value="individual">Individual</option>
                <option value="trust">Trust</option>
                <option value="estate">Estate</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Full Name</label>
              <input value={form.name} onChange={e => set('name', e.target.value)}
                     placeholder="Mary Smith" className={input} data-testid="input-bene-name" maxLength={120} />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Relationship</label>
              <input value={form.relationship} onChange={e => set('relationship', e.target.value)}
                     placeholder="Spouse" className={input} data-testid="input-bene-relationship" maxLength={60} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">SSN {initial && <span className="text-red-500">(re-enter to save)</span>}</label>
              <input value={form.ssn} onChange={e => handleSSNChange(e.target.value)}
                     placeholder="123-45-6789" inputMode="numeric"
                     className={input + ' font-mono'} data-testid="input-bene-ssn" />
              <p className="text-[10px] text-[#9CA3AF] mt-0.5">
                Encrypted at rest. Only last 4 digits are ever displayed.
              </p>
            </div>
            <div>
              <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Date of Birth</label>
              <input type="date" value={form.dateOfBirth} onChange={e => set('dateOfBirth', e.target.value)}
                     max={new Date().toISOString().slice(0,10)}
                     className={input + ' font-mono'} data-testid="input-bene-dob" />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">Mailing Address</label>
            <input value={form.street} onChange={e => set('street', e.target.value)}
                   placeholder="1500 Pennsylvania Ave NW" className={input + ' mb-2'} data-testid="input-bene-street" />
            <div className="grid grid-cols-6 gap-2">
              <input value={form.city} onChange={e => set('city', e.target.value)}
                     placeholder="City" className={input + ' col-span-3'} data-testid="input-bene-city" />
              <select value={form.state} onChange={e => set('state', e.target.value)}
                      className={input + ' col-span-1'} data-testid="input-bene-state">
                <option value="">—</option>
                {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <input value={form.zip} onChange={e => set('zip', e.target.value)}
                     placeholder="ZIP" className={input + ' col-span-2 font-mono'} data-testid="input-bene-zip" />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">
              Allocation %
              {!initial && remainingPct < 100 && (
                <span className="ml-2 font-mono font-bold text-[#1B2A4A]">(Tier remaining: {remainingPct}%)</span>
              )}
            </label>
            <input type="number" step="0.01" min="0" max="100"
                   value={form.allocationPct} onChange={e => set('allocationPct', e.target.value)}
                   className={input + ' font-mono'} data-testid="input-bene-allocation" />
          </div>

          <div className="flex gap-2 pt-2">
            <button onClick={onClose} disabled={saving}
                    className="flex-1 h-10 rounded text-xs font-medium text-[#6B7280] border border-[#D1D5DB] hover:bg-gray-50 disabled:opacity-50">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving}
                    data-testid="submit-beneficiary-form"
                    className="flex-1 h-10 rounded text-xs font-medium text-white bg-[#1B2A4A] hover:bg-[#0F1A33] disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {saving && <Loader2 size={12} className="animate-spin" />}
              {initial ? 'Save Changes' : 'Add Beneficiary'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────
// Main panel
// ────────────────────────────────────────────────────────────────
export const BeneficiariesPanel = () => {
  const [data, setData] = useState({ primary: [], contingent: [], validation: null });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [defaultTier, setDefaultTier] = useState('primary');
  const [generating, setGenerating] = useState(false);
  const [envelope, setEnvelope] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/participant/beneficiaries');
      setData(res.data);
      setErr('');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Unable to load beneficiaries.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const primaryTotal    = data.validation?.primaryTotal || 0;
  const contingentTotal = data.validation?.contingentTotal || 0;
  const canSubmit       = data.validation?.canSubmit || false;
  const hasContingent   = (data.contingent?.length || 0) > 0;
  const reviewOverdue   = data.validation?.reviewOverdue || false;
  const lastReviewed    = data.validation?.lastReviewedAt;

  const openAdd = (tier) => {
    setEditTarget(null);
    setDefaultTier(tier);
    setModalOpen(true);
  };
  const openEdit = (b) => { setEditTarget(b); setModalOpen(true); };

  const handleDelete = async (b) => {
    if (!window.confirm(`Remove ${b.name} from ${b.tier} beneficiaries?`)) return;
    setBusyId(b.id); setErr('');
    try {
      await api.delete(`/participant/beneficiaries/${b.id}`);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Unable to remove beneficiary.');
    } finally {
      setBusyId(null);
    }
  };

  const handleGenerateTSP3 = async () => {
    setGenerating(true); setErr(''); setEnvelope(null);
    try {
      const res = await api.post('/documents/generate/tsp3');
      setEnvelope(res.data);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Beneficiary Designation generation failed.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="beneficiaries-panel">
      {/* Permanent statutory disclosure */}
      <div className="panel p-4 border-l-4 border-l-[#B45309] bg-amber-50 border-amber-200"
           data-testid="beneficiary-statutory-notice" role="alert">
        <div className="flex items-start gap-3">
          <Gavel size={15} className="text-[#B45309] mt-0.5 shrink-0" />
          <div className="text-xs text-amber-900 leading-relaxed space-y-1">
            <p>
              <b>Your Beneficiary Designation beneficiary designation completely overrides your will and all
              other estate planning documents.</b>
            </p>
            <p>
              If you have no valid designation on file, your retirement balance is distributed in
              this statutory order <span className="font-mono text-[11px]">(5 U.S.C. § 8424)</span>:
            </p>
            <ol className="list-decimal pl-5 space-y-0.5 mt-1">
              <li>Widow or widower</li>
              <li>Children in equal shares; deceased child's share to their children</li>
              <li>Parents in equal shares</li>
              <li>Executor or administrator of the estate</li>
              <li>Next of kin under the laws of your state of domicile</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Overdue review warning */}
      {reviewOverdue && (
        <div className="panel p-3 bg-red-50 border-red-200" data-testid="review-overdue-notice">
          <div className="flex items-start gap-2">
            <Clock size={13} className="text-red-600 mt-0.5" />
            <p className="text-xs text-red-800">
              <b>Designation not reviewed in over 2 years.</b> Review and re-submit your Beneficiary Designation
              to ensure it reflects your current wishes.
            </p>
          </div>
        </div>
      )}

      {err && (
        <div className="panel p-3 border-red-200 bg-red-50" data-testid="beneficiaries-error">
          <p className="text-xs text-red-700">{err}</p>
        </div>
      )}

      {envelope && (
        <div className="panel p-4 bg-emerald-50 border-emerald-200" data-testid="tsp3-success-notice">
          <div className="flex items-start gap-2">
            <CheckCircle size={15} className="text-emerald-600 mt-0.5" />
            <div className="flex-1 text-xs text-emerald-900 leading-relaxed">
              <p className="font-semibold">Beneficiary Designation Envelope Sent</p>
              <p className="mt-1">{envelope.message}</p>
              <a href={envelope.envelopeUrl} target="_blank" rel="noopener noreferrer"
                 data-testid="tsp3-envelope-link"
                 className="inline-block mt-2 text-[11px] font-mono text-emerald-700 underline">
                Envelope {envelope.envelopeId} →
              </a>
              {envelope.mocked && (
                <p className="mt-2 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                  <b>MOCKED</b> — {envelope.mockedNotice}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* TIER PANELS */}
      {loading ? (
        <div className="panel p-6 animate-pulse" data-testid="beneficiaries-loading">
          <div className="h-4 bg-gray-100 rounded w-40 mb-3" />
          <div className="h-24 bg-gray-50 rounded" />
        </div>
      ) : (
        <>
          <TierSection
            title="Tier 1 · Primary Beneficiaries"
            subtitle="Receive if participant dies."
            tier="primary"
            items={data.primary}
            total={primaryTotal}
            isValid={data.validation?.primaryValid}
            required
            onAdd={() => openAdd('primary')}
            onEdit={openEdit}
            onDelete={handleDelete}
            busyId={busyId}
          />

          <TierSection
            title="Tier 2 · Contingent Beneficiaries"
            subtitle="Receive only if all primary beneficiaries predecease. Optional — leave empty if no contingent."
            tier="contingent"
            items={data.contingent}
            total={contingentTotal}
            isValid={data.validation?.contingentValid}
            required={false}
            onAdd={() => openAdd('contingent')}
            onEdit={openEdit}
            onDelete={handleDelete}
            busyId={busyId}
          />
        </>
      )}

      {/* Save & Generate Beneficiary Designation CTA */}
      <div className="panel p-4 bg-[#F8F9FA] border-[#E2E5E9]" data-testid="tsp3-cta-panel">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex-1 min-w-[240px]">
            <h3 className="text-sm font-semibold text-[#1B2A4A]">Generate Beneficiary Designation Form</h3>
            <p className="text-xs text-[#6B7280] mt-0.5">
              Your designation becomes legally binding only after you sign the Beneficiary Designation form
              via DocuSign. Both tier totals must equal 100% before submission.
            </p>
            {lastReviewed && (
              <p className="text-[10px] text-[#9CA3AF] font-mono mt-1">
                Last reviewed: {fmtDate(lastReviewed.slice(0, 10))}
              </p>
            )}
          </div>
          <button
            onClick={handleGenerateTSP3}
            disabled={!canSubmit || generating}
            data-testid="generate-tsp3-btn"
            title={canSubmit ? 'Send Beneficiary Designation to DocuSign' : 'Both tiers must total exactly 100%'}
            className="inline-flex items-center gap-2 h-10 px-5 rounded text-xs font-semibold text-white bg-[#1B2A4A] hover:bg-[#0F1A33] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {generating ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
            Save &amp; Generate Beneficiary Designation
          </button>
        </div>
      </div>

      {/* Footer disclosure */}
      <div className="panel p-4 bg-blue-50 border-blue-200" data-testid="beneficiary-footer-disclosure">
        <div className="flex items-start gap-2">
          <Info size={13} className="text-blue-600 mt-0.5 shrink-0" />
          <p className="text-[11px] text-blue-800 leading-relaxed">
            Surviving spouse may roll retirement balance to their own IRA tax-free. Non-spouse
            beneficiaries are subject to the <b>10-year distribution rule</b> under the
            SECURE Act. Minor children's inherited accounts have special rules —{' '}
            <b>consult a qualified tax advisor</b>.
          </p>
        </div>
      </div>

      <BeneficiaryFormModal
        open={modalOpen}
        initial={editTarget}
        defaultTier={defaultTier}
        remainingPct={
          defaultTier === 'primary'
            ? Math.max(100 - primaryTotal, 0)
            : Math.max(100 - contingentTotal, 0)
        }
        onClose={() => setModalOpen(false)}
        onSaved={load}
      />
    </div>
  );
};


// ────────────────────────────────────────────────────────────────
// Tier section subcomponent
// ────────────────────────────────────────────────────────────────
function TierSection({ title, subtitle, tier, items, total, isValid, required, onAdd, onEdit, onDelete, busyId }) {
  const isEmpty = items.length === 0;
  const showIncomplete = required ? !isValid : (items.length > 0 && !isValid);

  return (
    <div className="panel p-0 overflow-hidden" data-testid={`tier-section-${tier}`}>
      <div className="flex items-start justify-between flex-wrap gap-3 px-5 py-4 border-b border-[#E2E5E9] bg-[#FAFBFC]">
        <div className="flex-1 min-w-[240px]">
          <h3 className="text-sm font-semibold text-[#1B2A4A] uppercase tracking-wider">{title}</h3>
          <p className="text-[11px] text-[#6B7280] mt-0.5">{subtitle}</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Tier total meter */}
          <div className="text-right">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Allocated</span>
              <span
                className={
                  'font-mono font-bold text-sm ' +
                  (isValid
                    ? 'text-emerald-700'
                    : items.length === 0 && !required
                      ? 'text-[#9CA3AF]'
                      : 'text-[#B91C1C]')
                }
                data-testid={`tier-total-${tier}`}
              >
                {total.toFixed(2)}%
              </span>
              <span className="text-[10px] text-[#9CA3AF] font-mono">/ 100%</span>
            </div>
            {showIncomplete && (
              <p className="text-[10px] text-red-600 font-medium" data-testid={`tier-incomplete-${tier}`}>
                Must total exactly 100%
              </p>
            )}
            {isValid && items.length > 0 && (
              <p className="text-[10px] text-emerald-600 font-medium flex items-center gap-1 justify-end">
                <CheckCircle size={10} /> Complete
              </p>
            )}
          </div>
          <button onClick={onAdd} data-testid={`add-${tier}-btn`}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded text-[11px] font-medium text-[#1B2A4A] border border-[#D1D5DB] hover:border-[#1B2A4A] hover:bg-gray-50 transition-colors">
            <Plus size={11} /> Add
          </button>
        </div>
      </div>

      <div className="divide-y divide-[#F0F1F3]">
        {isEmpty ? (
          <div className="p-6 text-center text-xs text-[#9CA3AF]" data-testid={`tier-empty-${tier}`}>
            {required
              ? 'No primary beneficiaries added yet. Primary tier must total exactly 100%.'
              : 'No contingent beneficiaries. Contingent designation is optional.'}
          </div>
        ) : (
          items.map(b => (
            <BeneficiaryRow
              key={b.id} b={b} busy={busyId === b.id}
              onEdit={() => onEdit(b)}
              onDelete={() => onDelete(b)}
            />
          ))
        )}
      </div>
    </div>
  );
}


function BeneficiaryRow({ b, busy, onEdit, onDelete }) {
  const TypeIcon = TYPE_META[b.beneficiaryType]?.icon || User;
  const typeLabel = TYPE_META[b.beneficiaryType]?.label || 'Individual';

  return (
    <div className="px-5 py-4 flex items-start gap-4 flex-wrap" data-testid={`bene-row-${b.id}`}>
      <div className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center bg-[#F0F1F3] border border-[#E2E5E9]">
        <TypeIcon size={14} className="text-[#6B7280]" />
      </div>

      <div className="flex-1 min-w-[260px]">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-[#1B2A4A]" data-testid={`bene-name-${b.id}`}>
            {b.name}
          </span>
          <span className="text-xs text-[#6B7280]">({b.relationship})</span>
          <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-gray-100 border border-[#D1D5DB] text-[#6B7280] uppercase">
            {typeLabel}
          </span>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-[#6B7280] mt-1 font-mono">
          <span>SSN: •••-••-<span className="text-[#1B2A4A]" data-testid={`bene-ssn4-${b.id}`}>{b.ssnLast4}</span></span>
          <span>DOB: {fmtDate(b.dateOfBirth)}</span>
        </div>
        <p className="text-[11px] text-[#9CA3AF] mt-0.5">
          {b.street}, {b.city}, {b.state} {b.zip}
        </p>
      </div>

      <div className="flex items-center gap-3 shrink-0">
        <div className="text-right">
          <div className="font-mono text-lg font-bold text-[#1B2A4A]" data-testid={`bene-pct-${b.id}`}>
            {b.allocationPct.toFixed(2)}%
          </div>
          <div className="text-[9px] text-[#9CA3AF] uppercase tracking-wider">Allocation</div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onEdit} disabled={busy}
                  data-testid={`edit-bene-${b.id}`}
                  title="Edit"
                  className="h-7 w-7 rounded text-[#6B7280] hover:text-[#1B2A4A] hover:bg-gray-50 border border-transparent hover:border-[#D1D5DB] inline-flex items-center justify-center disabled:opacity-50">
            <Edit3 size={12} />
          </button>
          <button onClick={onDelete} disabled={busy}
                  data-testid={`delete-bene-${b.id}`}
                  title="Remove"
                  className="h-7 w-7 rounded text-[#9CA3AF] hover:text-red-600 hover:bg-red-50 border border-transparent hover:border-red-200 inline-flex items-center justify-center disabled:opacity-50">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
          </button>
        </div>
      </div>
    </div>
  );
}

export default BeneficiariesPanel;
